<template>
  <div>
    <div class="flex h-full flex-col">
      <div :class="width ? 'w-full' : 'w-full xl:w-1/4'">
        <Button
          :label="$t('home.add_device')"
          icon="pi pi-plus-circle"
          class="w-full btn-ghost"
          variant="outlined"
          severity="secondary"
          @click="showModalInternal = true"
        />
      </div>
    </div>

    <Dialog
      v-model:visible="showModalInternal"
      modal
      class="add-device-dialog"
      :style="{ width: '500px' }"
      :breakpoints="{ '640px': '95vw' }"
      :dismissable-mask="true"
      @hide="closeModal"
    >
      <template #header>
        <div>
          <p class="header-title m-0 font-semibold">
            {{ headerTitle }}
          </p>
          <p class="header-subtitle m-0 text-sm text-[#6c757d]">
            {{ headerSubtitle }}
          </p>
        </div>
      </template>

      <DeviceSetupComponent
        ref="setupRef"
        :pending-device="pendingDevice"
        :show-internal-name="showInternalName"
        @device-added="onDeviceAdded"
        @close="closeModal"
      />

      <template #footer>
        <div class="flex w-full flex-col gap-3">
          <Button
            v-if="setupRef && setupRef.step === 1"
            :label="$t('common.continue')"
            :loading="setupRef.isLoading"
            :disabled="!setupRef.canStartConnection"
            class="w-full"
            size="large"
            @click.stop="setupRef.navigateToStep(2)"
          />
          <template v-else-if="setupRef && setupRef.step === 2">
            <Button
              v-if="setupRef.showAddNewDevice"
              :label="$t('home.add_another_device')"
              icon="pi pi-plus"
              variant="outlined"
              class="w-full"
              size="large"
              @click.stop="setupRef.addAnotherDevice()"
            />
            <Button
              :label="$t('common.close')"
              class="w-full"
              size="large"
              @click.stop="closeModal"
            />
          </template>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';

import DeviceSetupComponent from '@/components/home/DeviceSetupComponent.vue';
import { Device } from '@/types/device';

export default defineComponent({
  name: 'AddDeviceComponent',
  components: {
    Button,
    Dialog,
    DeviceSetupComponent,
  },
  props: {
    pendingDevice: {
      type: Object as PropType<Device | null>,
      default: null,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
    width: {
      type: Boolean,
      default: false,
    },
    showInternalName: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['device-added', 'close-modal'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const showModalInternal = ref(false);
    const isLoading = ref(false);
    const setupRef = ref<InstanceType<typeof DeviceSetupComponent> | null>(null);

    const headerTitle = computed(() => {
      if (!setupRef.value || setupRef.value.step === 1) {
        return t('device.connect_device');
      }
      return setupRef.value.showConnectDeviceHeader
        ? t('device.connect_device')
        : t('home.device_created');
    });

    const headerSubtitle = computed(() => {
      if (!setupRef.value || setupRef.value.step === 1) {
        return t('device.choose_device');
      }
      return t('home.copy_key');
    });

    const onDeviceAdded = (newDevice: Device) => {
      emit('device-added', newDevice);
    };

    watch(() => props.showModal, (newVal) => {
      showModalInternal.value = newVal;
    });

    watch(() => props.pendingDevice, (newDevice) => {
      if (newDevice) {
        showModalInternal.value = true;
      }
    });

    const closeModal = () => {
      showModalInternal.value = false;
      emit('close-modal');
    };

    return {
      onDeviceAdded,
      showModalInternal,
      isLoading,
      closeModal,
      setupRef,
      headerTitle,
      headerSubtitle,
    };
  },
});
</script>

<style scoped>
@media (max-width: 768px) {
  :deep(.add-device-dialog.p-dialog) {
    width: 100vw;
    height: 100dvh;
    max-height: 100dvh;
    max-width: 100vw;
    margin: 0;
    border-radius: 0;
  }

  :deep(.add-device-dialog .p-dialog-content) {
    flex: 1;
  }
}
</style>
